"use client";

import { useActionState, useEffect, useState, type FormEvent, Suspense } from "react";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Card } from "@astryxdesign/core/Card";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Button } from "@astryxdesign/core/Button";
import { Link } from "@astryxdesign/core/Link";
import { Banner } from "@astryxdesign/core/Banner";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { registerAction, type FormState } from "../actions";
import { CONSENT_LINKS } from "@/lib/consent/constants";
import { WEBMCP_PARAMS, webmcpParam } from "@/lib/webmcp/forms";
import { readBitrix24GuestTrace } from "@/lib/bitrix24/tracking";
import { readStoredUtm } from "@/lib/analytics/utm";
import { RuPhoneInput } from "@/components/forms/ru-phone-input";
import { PromoAttachedBanner } from "@/components/promo/promo-attached-banner";
import { readStoredPromo } from "@/lib/billing/promo-cookie";
import { PROMO_QUERY_KEY } from "@/lib/billing/promo-link";
import {
  OAuthDivider,
  OAuthSignInButtons,
} from "@/components/auth/oauth-sign-in";

const initialState: FormState = { status: "idle" };

function ConsentCheckbox({
  label,
  href,
  linkLabel,
  value,
  onChange,
  error,
}: {
  label: string;
  href: string;
  linkLabel: string;
  value: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div
        className="flex min-w-0 cursor-pointer items-center gap-2"
        onClick={() => onChange(!value)}
      >
        <div
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <CheckboxInput
            label={label}
            isLabelHidden
            value={value}
            onChange={onChange}
            size="sm"
            width="auto"
          />
        </div>
        <p className="min-w-0 select-none text-xs leading-tight text-[var(--color-text-primary)]">
          {label}{" "}
          <Link
            href={href}
            hasUnderline
            type="inherit"
            color="accent"
            onClick={(e) => e.stopPropagation()}
          >
            {linkLabel}
          </Link>
        </p>
      </div>
      {error ? (
        <Text
          type="supporting"
          size="sm"
          className="pl-7 text-[var(--color-text-danger,#b91c1c)]"
        >
          {error}
        </Text>
      ) : null}
    </div>
  );
}

type RegisterFormProps = {
  /** Уже принято через баннер cookies (cookie rs_consent_cookies). */
  cookiesAlreadyAccepted?: boolean;
  yandexEnabled?: boolean;
  vkEnabled?: boolean;
};

export function RegisterForm({
  cookiesAlreadyAccepted = false,
  yandexEnabled = false,
  vkEnabled = false,
}: RegisterFormProps) {
  const [state, formAction, pending] = useActionState(
    registerAction,
    initialState,
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [consentCookies, setConsentCookies] = useState(cookiesAlreadyAccepted);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentNewsletter, setConsentNewsletter] = useState(false);
  const [loginHref, setLoginHref] = useState("/login");

  useEffect(() => {
    const promo = readStoredPromo();
    setLoginHref(
      promo?.code
        ? `/login?${PROMO_QUERY_KEY}=${encodeURIComponent(promo.code)}`
        : "/login",
    );
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const trace = readBitrix24GuestTrace();
    if (trace) formData.set("bitrixTrace", trace);
    const utm = readStoredUtm();
    if (utm) formData.set("bitrixUtm", JSON.stringify(utm));
    formData.set(
      "landingPath",
      `${window.location.pathname}${window.location.search}`,
    );
    if (document.referrer) formData.set("referrer", document.referrer);
    formAction(formData);
  }

  if (state.status === "success") {
    return (
      <>
        <Heading level={1} type="display-3">
          Проверьте почту
        </Heading>
        <Banner status="success" title={state.message ?? ""} container="card" />
        <Suspense fallback={null}>
          <PromoAttachedBanner variant="after_verify" />
        </Suspense>
        <HStack gap={1} justify="center">
          <Text color="secondary">Уже подтвердили?</Text>
          <Link href={loginHref}>Войти</Link>
        </HStack>
      </>
    );
  }

  return (
    <>
      <VStack gap={2} align="start">
        <Heading level={1} type="display-3">
          Регистрация
        </Heading>
        <Text color="secondary">
          {yandexEnabled || vkEnabled
            ? "Создайте аккаунт по email или через Яндекс ID / VK ID."
            : "Создайте аккаунт, подтвердите email и получите первый отчёт."}
        </Text>
      </VStack>

      <Suspense fallback={null}>
        <PromoAttachedBanner variant="form" />
      </Suspense>

      {state.status === "error" && state.message ? (
        <Banner status="error" title={state.message} container="card" />
      ) : null}

      <Card padding={6} width="100%">
        <VStack gap={5} align="stretch" width="100%">
          {yandexEnabled || vkEnabled ? (
            <VStack gap={3} align="stretch" width="100%">
              <OAuthSignInButtons
                yandexEnabled={yandexEnabled}
                vkEnabled={vkEnabled}
                yandexLabel="Регистрация с Яндекс ID"
                vkLabel="Регистрация с VK ID"
              />
              <OAuthDivider />
            </VStack>
          ) : null}
        <form
          onSubmit={handleSubmit}
          toolname="registerAccount"
          tooldescription="Создать аккаунт Идеалитика: имя, email, пароль и согласия. После регистрации нужно подтвердить email."
        >
          <input
            type="hidden"
            name="consentCookies"
            value={
              consentCookies || cookiesAlreadyAccepted ? "true" : "false"
            }
            {...webmcpParam(WEBMCP_PARAMS.consentCookies)}
          />
          <input
            type="hidden"
            name="consentPrivacy"
            value={consentPrivacy ? "true" : "false"}
            {...webmcpParam(WEBMCP_PARAMS.consentPrivacy)}
          />
          <input
            type="hidden"
            name="consentNewsletter"
            value={consentNewsletter ? "true" : "false"}
            {...webmcpParam(WEBMCP_PARAMS.consentNewsletter)}
          />
          <VStack gap={4}>
            <TextInput
              type="text"
              label="Имя"
              htmlName="name"
              value={name}
              onChange={setName}
              placeholder="Как к вам обращаться"
              status={
                state.fieldErrors?.name
                  ? { type: "error", message: state.fieldErrors.name }
                  : undefined
              }
              isRequired
              hasAutoFocus
              {...webmcpParam(WEBMCP_PARAMS.accountName)}
            />
            <TextInput
              type="email"
              label="Email"
              htmlName="email"
              value={email}
              onChange={setEmail}
              placeholder="you@company.ru"
              status={
                state.fieldErrors?.email
                  ? { type: "error", message: state.fieldErrors.email }
                  : undefined
              }
              isRequired
              {...webmcpParam(WEBMCP_PARAMS.accountEmail)}
            />
            <RuPhoneInput
              value={phone}
              onChange={setPhone}
              isRequired
              error={state.fieldErrors?.phone ?? null}
            />
            <TextInput
              type="password"
              label="Пароль"
              htmlName="password"
              value={password}
              onChange={setPassword}
              description="Минимум 8 символов"
              status={
                state.fieldErrors?.password
                  ? { type: "error", message: state.fieldErrors.password }
                  : undefined
              }
              isRequired
              {...webmcpParam(WEBMCP_PARAMS.accountPassword)}
            />
            <TextInput
              type="password"
              label="Повторите пароль"
              htmlName="passwordConfirm"
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              status={
                state.fieldErrors?.passwordConfirm
                  ? { type: "error", message: state.fieldErrors.passwordConfirm }
                  : undefined
              }
              isRequired
              {...webmcpParam(WEBMCP_PARAMS.accountPasswordConfirm)}
            />

            <VStack gap={1} align="start" width="100%" className="min-w-0">
              {!cookiesAlreadyAccepted ? (
                <ConsentCheckbox
                  label="Согласие на cookies"
                  href={CONSENT_LINKS.cookies}
                  linkLabel="Подробнее"
                  value={consentCookies}
                  onChange={setConsentCookies}
                  error={state.fieldErrors?.consentCookies}
                />
              ) : null}
              <ConsentCheckbox
                label="Обработка персональных данных"
                href={CONSENT_LINKS.privacy}
                linkLabel="Подробнее"
                value={consentPrivacy}
                onChange={setConsentPrivacy}
                error={state.fieldErrors?.consentPrivacy}
              />
              <ConsentCheckbox
                label="Рассылка (необязательно)"
                href={CONSENT_LINKS.newsletter}
                linkLabel="Подробнее"
                value={consentNewsletter}
                onChange={setConsentNewsletter}
              />
            </VStack>

            <Button
              label="Создать аккаунт"
              variant="primary"
              type="submit"
              size="lg"
              isLoading={pending}
            />
          </VStack>
        </form>
        </VStack>
      </Card>

      <HStack gap={1} justify="center">
        <Text color="secondary">Уже есть аккаунт?</Text>
        <Link href={loginHref}>Войти</Link>
      </HStack>
    </>
  );
}
